import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Typography,
  Grid,
  Divider,
  TextField,
  Button,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useDispatch, useSelector } from "react-redux";
import { SupportTickets } from "../../app/slices/supportTicketsSlice";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const STATUS_OPTIONS = [
  { label: "New", value: "new" },
  { label: "In Progress", value: "in_progress" },
  { label: "Resolved", value: "resolved" },
];

const statusChipProps = (status) => {
  switch (status) {
    case "new":
      return { label: "New", color: "info" };
    case "in_progress":
      return { label: "In Progress", color: "warning" };
    case "resolved":
      return { label: "Resolved", color: "success" };
    default:
      return { label: status, color: "default" };
  }
};

const borders_shadow = {
  borderRadius: "12px",
  boxShadow:
    "rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px",
};

export const TicketDetails = ({ data, id }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [comment, setComment] = useState("");
  const {
    addComment: { loading: commentLoading },
    updateStatus: { loading: statusLoading },
  } = useSelector((state) => state.supportTickets);

  if (!data) {
    return (
      <Box m={2}>
        <Typography variant="h6" color="text.secondary">
          Ticket not found.
        </Typography>
      </Box>
    );
  }

  const chip = statusChipProps(data.status);

  const handleStatusChange = (e) => {
    dispatch(SupportTickets.updateStatus(id, e.target.value));
  };

  const handleAddComment = () => {
    if (!comment.trim()) return;
    dispatch(SupportTickets.addComment(id, comment.trim()));
    setComment("");
  };

  return (
    <Box m={2}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/tickets")}
        sx={{ mb: 2 }}
        variant="outlined"
        size="small"
      >
        Back to Tickets
      </Button>

      <Grid container spacing={2}>
        {/* Header Card */}
        <Grid item xs={12}>
          <Card sx={borders_shadow}>
            <CardHeader
              avatar={<ConfirmationNumberIcon color="primary" fontSize="large" />}
              title={
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="h6" fontWeight="bold">
                    {data.title}
                  </Typography>
                  <Chip
                    label={chip.label}
                    color={chip.color}
                    size="small"
                  />
                </Box>
              }
              subheader={
                <Typography variant="caption" color="text.secondary">
                  {data.ticket_number}
                </Typography>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Submitted By
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {data.user?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {data.user?.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {dayjs(data.created_at).format("YYYY-MM-DD HH:mm")}
                  </Typography>
                </Grid>
                {data.resolved_at && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Resolved At
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {dayjs(data.resolved_at).format("YYYY-MM-DD HH:mm")}
                    </Typography>
                    {data.resolved_by && (
                      <Typography variant="caption" color="text.secondary">
                        by {data.resolved_by.name}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Description + Status change */}
        <Grid item xs={12} md={8}>
          <Card sx={{ ...borders_shadow, height: "100%" }}>
            <CardHeader
              title={
                <Typography variant="subtitle1" fontWeight="bold">
                  Description
                </Typography>
              }
            />
            <CardContent>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {data.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Management */}
        <Grid item xs={12} md={4}>
          <Card sx={{ ...borders_shadow, height: "100%" }}>
            <CardHeader
              title={
                <Typography variant="subtitle1" fontWeight="bold">
                  Manage Status
                </Typography>
              }
            />
            <CardContent>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={data.status}
                  label="Status"
                  onChange={handleStatusChange}
                  disabled={statusLoading}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {statusLoading && (
                <Box display="flex" justifyContent="center" mt={1}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Comments Section */}
        <Grid item xs={12}>
          <Card sx={borders_shadow}>
            <CardHeader
              title={
                <Typography variant="subtitle1" fontWeight="bold">
                  Comments ({data.comments_count || data.comments?.length || 0})
                </Typography>
              }
            />
            <CardContent>
              {/* Comment list */}
              <Box display="flex" flexDirection="column" gap={2} mb={3}>
                {data.comments && data.comments.length > 0 ? (
                  data.comments.map((c) => (
                    <Box key={c.id}>
                      <Box
                        display="flex"
                        gap={2}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: c.is_admin_reply ? "#fef3e2" : "#f4f6f8",
                          border: c.is_admin_reply
                            ? "1px solid #f5a623"
                            : "1px solid #e0e0e0",
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: c.is_admin_reply ? "#a62639" : "#607d8b",
                            width: 36,
                            height: 36,
                          }}
                        >
                          {c.is_admin_reply ? (
                            <AdminPanelSettingsIcon fontSize="small" />
                          ) : (
                            <PersonIcon fontSize="small" />
                          )}
                        </Avatar>
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {c.user?.name}
                            </Typography>
                            <Chip
                              label={c.is_admin_reply ? "Admin" : c.user?.type}
                              size="small"
                              color={c.is_admin_reply ? "error" : "default"}
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(c.created_at).format("YYYY-MM-DD HH:mm")}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {c.comment}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No comments yet.
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Reply form */}
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  Add a Reply
                </Typography>
                <TextField
                  multiline
                  minRows={3}
                  maxRows={6}
                  fullWidth
                  placeholder="Write your reply... (max 2000 characters)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  inputProps={{ maxLength: 2000 }}
                  size="small"
                />
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    {comment.length}/2000
                  </Typography>
                  <Button
                    variant="contained"
                    endIcon={
                      commentLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SendIcon />
                      )
                    }
                    onClick={handleAddComment}
                    disabled={!comment.trim() || commentLoading}
                    size="small"
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TicketDetails;
